import React from 'react';

const FONT_MAP = {
  professional: "'Helvetica Neue', Arial, sans-serif",
  modern: "'DM Sans', sans-serif",
  classic: "Georgia, 'Times New Roman', serif",
  minimal: "'Courier New', Courier, monospace",
};

const SAMPLE_DATA = {
  company_name: 'Wilcart Services',
  client_name: 'John Smith',
  client_phone: '(305) 555-0100',
  client_email: 'john.smith@email.com',
  client_address: '123 Oak Street, Miami FL 33101',
  service_description: 'Professional moving service',
  location: '123 Main St, Miami FL',
  scheduled_date: 'May 15, 2026',
  delivery_date: 'June 4, 2026',
  total_cost: '1,250.00',
  labor_cost: '900.00',
  materials_cost: '200.00',
  subtotal: '1,100.00',
  tax: '150.00',
  total: '1,250.00',
  max_amount_due: '20,000.00',
  balance_due: '0.00',
  invoice_number: 'INV-001234',
  estimate_number: 'EST-001234',
  date: new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
  due_date: new Date(Date.now() + 30 * 86400000)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
  duration: '4',
  hourly_rate: '95.00',
  payment_status: 'Unpaid',
  payment_terms: 'Due upon completion',
  pickup_address: '123 Oak Street, Miami FL 33101',
  delivery_address: '456 Pine Avenue, Fort Lauderdale FL 33301',
  origin_address: '123 Oak Street, Miami FL 33101',
  destination_address: '456 Pine Avenue, Fort Lauderdale FL 33301',
  from_address: '123 Oak Street, Miami FL 33101',
  from_phone: '(305) 555-0100',
  to_address: '456 Pine Avenue, Fort Lauderdale FL 33301',
  to_phone: '(954) 555-0200',
  move_from: '123 Oak Street, Miami FL',
  move_to: '456 Pine Ave, Fort Lauderdale FL',
  vehicle_type: 'SUV / Crossover',
  vehicle_make_model: 'BMW X5',
  vehicle_year: '2022',
  vehicle_color: 'Pearl White',
  paint_condition: 'Good',
  service_location: '789 Client Driveway, Miami FL 33101',
  job_title: 'Residential Moving Service',
  job_number: 'A1B2C3',
  job_date: new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
  job_status: 'Scheduled',
  service_type: 'Standard Move',
  home_size: '3 Bedrooms',
  issue_type: 'Repair',
  system_type: 'Central AC',
  frequency: 'One-time',
  project_type: 'Renovation',
  num_movers: '3',
  num_trucks: '1',
  crew_size: '3',
  assigned_workers: 'Mike Johnson',
  start_time: '9:00 AM',
};

const applyVariables = (html, orgName) => {
  if (!html) return '';
  let result = html;
  const data = { ...SAMPLE_DATA, company_name: orgName || SAMPLE_DATA?.company_name };

  // First: strip var-chip span wrappers (keep inner {{variable}} text)
  result = result?.replace(/<span[^>]*class="var-chip"[^>]*contenteditable="false"[^>]*>([\s\S]*?)<\/span>/g, '$1');
  result = result?.replace(/<span[^>]*class="var-chip"[^>]*>([\s\S]*?)<\/span>/g, '$1');

  // Then replace all {{variable}} with styled sample values
  Object.entries(data)?.forEach(([key, val]) => {
    result = result?.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), `<span style="color:#2563eb;font-weight:600;">${val}</span>`);
  });

  // Any remaining unreplaced variables — show in gray italic
  result = result?.replace(/\{\{([^}]+)\}\}/g, '<span style="color:#9ca3af;font-style:italic;">[$1]</span>');

  return result;
};

const textToPreviewHtml = (text) => {
  if (!text) return '';
  if (text?.trim()?.startsWith('<')) return text;
  return text?.split('\n')?.map(line => {
    if (!line?.trim()) return '<p style="margin:2px 0;height:8px;"></p>';
    const escaped = line?.replace(/&/g, '&amp;')?.replace(/</g, '&lt;')?.replace(/>/g, '&gt;');
    if (/^━+$|^─+$/?.test(line?.trim())) {
      return '<hr style="border:none;border-top:1px solid #e5e7eb;margin:8px 0;"/>';
    }
    if (/^[A-Z][A-Z\s&]+$/?.test(line?.trim()) && line?.trim()?.length > 2 && line?.trim()?.length < 40) {
      return `<p style="font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;margin:10px 0 4px;">${escaped}</p>`;
    }
    return `<p style="margin:2px 0;font-size:12px;">${escaped}</p>`;
  })?.join('');
};

const SmartDocumentPreview = ({ template, orgName }) => {
  const fontFamily = FONT_MAP?.[template?.font_style || 'professional'];
  const showLogo = template?.show_logo !== false;
  const logoUrl = template?.logo_url;
  const templateType = template?.template_type;

  const rawContent = template?.content || '';
  const htmlContent = rawContent?.trim()?.startsWith('<') ? rawContent : textToPreviewHtml(rawContent);
  const renderedContent = applyVariables(htmlContent, orgName);

  const docTitle = templateType === 'invoice' ? 'INVOICE'
    : templateType === 'estimate' ? 'ESTIMATE'
    : templateType === 'agreement' ? 'SERVICE AGREEMENT'
    : templateType === 'email'? 'EMAIL TEMPLATE' :'SMS TEMPLATE';

  return (
    <div
      className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200"
      style={{ fontFamily }}
    >
      {/* Clean professional header — no color block */}
      <div className="px-8 pt-8 pb-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {showLogo && logoUrl ? (
              <img
                src={logoUrl}
                alt={`${orgName || 'Company'} logo`}
                className="h-12 object-contain"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-xs font-bold uppercase">
                {(orgName || 'Co')?.slice(0, 2)}
              </div>
            )}
            <div>
              <div className="text-sm font-semibold text-gray-900">{orgName || 'Your Company'}</div>
              <div className="text-xs text-gray-400 mt-0.5">{template?.payment_terms || 'Due upon completion'}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900 tracking-tight">{docTitle}</div>
            <div className="text-xs text-gray-400 mt-1">
              {new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            {templateType === 'invoice' && <div className="text-xs text-gray-500 font-mono mt-0.5">INV-001234</div>}
            {templateType === 'estimate' && <div className="text-xs text-gray-500 font-mono mt-0.5">EST-001234</div>}
          </div>
        </div>
      </div>

      {/* Document Body */}
      <div className="px-8 py-6 text-sm text-gray-800 leading-relaxed" style={{ minHeight: '350px' }}>
        <div
          className="preview-content"
          dangerouslySetInnerHTML={{ __html: renderedContent }}
          style={{ fontSize: '13px', lineHeight: '1.6' }}
        />
      </div>

      {/* Footer */}
      <div className="px-8 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
        <span className="text-xs text-gray-400">
          {orgName || 'Your Company'} — {template?.payment_terms || 'Due upon completion'}
        </span>
        <span className="text-xs text-gray-400">{new Date()?.toLocaleDateString()}</span>
      </div>

      <style>{`
        .preview-content h1 { font-size: 1.4rem; font-weight: 700; margin: 10px 0 5px; color: #111827; }
        .preview-content h2 { font-size: 1.1rem; font-weight: 600; margin: 8px 0 4px; color: #1f2937; }
        .preview-content h3 { font-size: 0.95rem; font-weight: 600; margin: 6px 0 3px; color: #374151; }
        .preview-content p { margin: 3px 0; }
        .preview-content ul { list-style: disc; padding-left: 18px; margin: 4px 0; }
        .preview-content ol { list-style: decimal; padding-left: 18px; margin: 4px 0; }
        .preview-content li { margin: 2px 0; }
        .preview-content table { width: 100%; border-collapse: collapse; margin: 8px 0; }
        .preview-content td, .preview-content th { border: 1px solid #e5e7eb; padding: 5px 8px; font-size: 12px; }
        .preview-content th { background: #f9fafb; font-weight: 600; }
        .preview-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 8px 0; }
        .preview-content strong, .preview-content b { font-weight: 700; }
        .preview-content em, .preview-content i { font-style: italic; }
        .preview-content u { text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default SmartDocumentPreview;
